import { SaveService } from "@highmarch/app";
import { IdbBackend, MemoryBackend, SaveBackend, localStorageMirror } from "./backends";
import { DurableSaveStore } from "./durableSaveStore";
import { WriterLock } from "./writerLock";
import { isNativeShell } from "../platform/shell";
import { PreferencesBackend } from "../platform/preferencesBackend";

/**
 * Opens the game's save store (main.ts, before the save loads): the durable backend for this platform, the
 * localStorage mirror and the single-writer lock. Never rejects: when no durable backend opens (a private window
 * refusing IndexedDB) the save is kept in the mirror alone, as before this store existed.
 */
export async function openSaveStore(opts: { backend?: () => Promise<SaveBackend>; log?: (level: "info" | "warning" | "error", message: string) => void } = {}): Promise<DurableSaveStore> {
  let backend: SaveBackend;
  try {
    backend = await (opts.backend ?? platformBackend)();
  } catch (err) {
    opts.log?.("warning", `save: no durable storage (${(err as Error).message}); keeping the save in localStorage only`);
    backend = new MemoryBackend();
  }
  const lock = new WriterLock(WriterLock.browserChannel(), randomTabId());
  // Claim before reading: an older tab flushes its last write first, so this tab reads the latest save.
  await lock.claim();
  const store = await DurableSaveStore.open({
    backend, mirror: localStorageMirror(), keys: [SaveService.Key, SaveService.BackupKey], corruptBackupKey: SaveService.BackupKey,
    lock, shrink: dropSuspendedBattle, log: opts.log,
  });
  // Back from the back-forward cache: another tab may have played meanwhile. Reload onto its save rather than
  // overwrite it with this page's older one; otherwise take the save back as the tab in front.
  globalThis.addEventListener?.("pageshow", (e) => {
    if (!(e as PageTransitionEvent).persisted) return;
    if (store.isStale()) location.reload();
    else void lock.claim();
  });
  return (current = store);
}

/** The iOS shell keeps the save in Preferences (UserDefaults); the browser in IndexedDB. */
function platformBackend(): Promise<SaveBackend> {
  return isNativeShell() ? PreferencesBackend.open() : IdbBackend.open();
}

let current: DurableSaveStore | null = null;
/** The open store (diagnostics, the save notices), or null before main.ts opened it. */
export function saveStore(): DurableSaveStore | null { return current; }

/** How long a reload waits for the save's backend writes: a backend that never answers must not trap the player. */
export const ReloadFlushMaxMs = 3000;

/**
 * Reloads the page once the writes made so far have reached the durable backend (Settings → Reset save, a save
 * import). Reloading at once aborted the backend's transaction, and the old save came back at the next open. The store
 * is frozen first, so nothing the page does on its way out (pagehide suspends a battle) writes the old save back.
 */
export async function reloadWhenSaved(store: DurableSaveStore | null = current, reload: () => void = () => location.reload()): Promise<void> {
  store?.freeze();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([store?.flush(), new Promise<void>((r) => { timer = setTimeout(r, ReloadFlushMaxMs); })]);
  } finally {
    clearTimeout(timer);
    reload();
  }
}

/**
 * The size guard's shrink: a save over the limit drops its suspended battle (the replay log of a level left mid-way,
 * `SaveRecord.suspended`), the one part that grows with play and that can be lost without losing progress: the player
 * restarts that level instead of continuing it.
 */
export function dropSuspendedBattle(key: string, json: string): { json: string; dropped: string } | null {
  if (key !== SaveService.Key) return null;
  try {
    const doc = JSON.parse(json) as { suspended?: unknown };
    if (doc.suspended === undefined) return null;
    delete doc.suspended;
    return { json: JSON.stringify(doc), dropped: "the suspended battle" };
  } catch { return null; }
}

function randomTabId(): string {
  try { return crypto.randomUUID(); } catch { return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`; }
}
